import { usePermissionData } from "AppContext"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/Loader"
import moment from "moment"
import PermittedComponent from "PermittedComponent"
import React, { useEffect, useMemo, useState } from "react"
import { FaCheck, FaEdit, FaFileExcel, FaInfoCircle } from "react-icons/fa"
import { FaX } from "react-icons/fa6"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  PopoverBody,
  PopoverHeader,
  Row,
  Table,
  UncontrolledPopover,
} from "reactstrap"
import Swal from "sweetalert2"
import * as XLSX from "xlsx"
import {
  fetchData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"

const Leaves = () => {
  document.title = "Leaves | Vintage Infra Equipment Sol PVT LTD"

  const [loading, setLoading] = useState(false)

  /* ================== PAGINATION + SEARCH ================== */
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [monthFilter, setMonthFilter] = useState(String(moment().month() + 1))
  const [yearFilter, setYearFilter] = useState(String(moment().year()))

  /* ================== LEAVE DATA ================== */
  const [leaveData, setLeaveData] = useState([])

  /* ================== USERS (EMPLOYEES) ================== */
  const [employees, setEmployees] = useState([])

  // 👉 PLACEHOLDER FUNCTION – ADD API LATER
  const fetchEmployees = async () => {
    const APIdata = await fetchData(setLoading, "users")
    if (APIdata?.success) {
      setEmployees(APIdata?.data)
    }
  }

  const fetchLeaves = async () => {
    const APIdata = await fetchData(setLoading, "leave")
    if (APIdata?.success) {
      setLeaveData(APIdata?.data)
    }
  }

  useEffect(() => {
    fetchEmployees()
    fetchLeaves()
  }, [])

  /* ================== ADD LEAVE MODAL ================== */
  const [modalOpen, setModalOpen] = useState(false)

  const [newLeave, setNewLeave] = useState({
    employeeId: "",
    leaveType: "",
    fromDate: "",
    toDate: "",
    leaveDate: "",
    workedDate: "",
    reason: "",
  })

  const handleChange = e => {
    const { name, value } = e.target
    if (name === "leaveType") {
      setNewLeave(prev => ({
        ...prev,
        leaveType: value,
        fromDate: "",
        toDate: "",
        leaveDate: "",
        workedDate: "",
      }))
      return
    }
    setNewLeave(prev => ({ ...prev, [name]: value }))
  }

  /* ================== EDIT LEAVE MODAL ================== */
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editLeave, setEditLeave] = useState({
    id: "",
    employeeId: "",
    leaveType: "",
    fromDate: "",
    toDate: "",
    leaveDate: "",
    workedDate: "",
    reason: "",
  })

  const handleEditOpen = item => {
    setEditLeave({
      id: item.id,
      employeeId: String(item.userId || item.user?.id || ""),
      leaveType: item.type || "",
      fromDate: item.fromDate ? moment(item.fromDate).format("YYYY-MM-DD") : "",
      toDate: item.toDate ? moment(item.toDate).format("YYYY-MM-DD") : "",
      leaveDate: item.adjustmentDate
        ? moment(item.adjustmentDate).format("YYYY-MM-DD")
        : "",
      workedDate: item.leaveDate
        ? moment(item.leaveDate).format("YYYY-MM-DD")
        : "",
      reason: item.reason || "",
    })
    setEditModalOpen(true)
  }

  const handleEditChange = e => {
    const { name, value } = e.target
    if (name === "leaveType") {
      setEditLeave(prev => ({
        ...prev,
        leaveType: value,
        fromDate: "",
        toDate: "",
        leaveDate: "",
        workedDate: "",
      }))
      return
    }
    setEditLeave(prev => ({ ...prev, [name]: value }))
  }

  const handleEditLeave = async () => {
    const { id, employeeId, leaveType, fromDate, toDate, leaveDate, workedDate, reason } = editLeave

    if (!employeeId) return Swal.fire("Required", "Please select Employee", "warning")
    if (!leaveType) return Swal.fire("Required", "Please select Leave Type", "warning")
    if (!reason.trim()) return Swal.fire("Required", "Please enter Reason", "warning")

    if (leaveType === "paidOff") {
      if (!leaveDate) return Swal.fire("Required", "Please select Leave Date", "warning")
      if (!workedDate) return Swal.fire("Required", "Please select Worked Date", "warning")
      if (new Date(leaveDate) > new Date(workedDate))
        return Swal.fire("Invalid Dates", "Worked Date cannot be before Leave Date", "warning")
    }

    if (leaveType === "casualLeave" || leaveType === "sickLeave") {
      if (!fromDate) return Swal.fire("Required", "Please select From Date", "warning")
      if (!toDate) return Swal.fire("Required", "Please select To Date", "warning")
      if (new Date(fromDate) > new Date(toDate))
        return Swal.fire("Invalid Dates", "From Date cannot be after To Date", "warning")
    }

    const payload = {
      userId: employeeId,
      type: leaveType,
      reason,
      ...(leaveType === "paidOff"
        ? { leaveDate: workedDate, adjustmentDate: leaveDate }
        : { fromDate, toDate }),
    }

    const APIdata = await FetchPutData(setLoading, "leave", id, payload)

    if (APIdata?.data?.success) {
      Swal.fire("Success", APIdata?.data?.message, "success")
      setEditModalOpen(false)
      fetchLeaves()
    } else {
      Swal.fire("Error", APIdata?.response?.data?.message || "Something went wrong", "error")
    }
  }

  /* ================== ADD LEAVE SUBMIT ================== */
  const handleAddLeave = async () => {
    const {
      employeeId,
      leaveType,
      fromDate,
      toDate,
      leaveDate,
      workedDate,
      reason,
    } = newLeave

    if (!employeeId) {
      return Swal.fire("Required", "Please select Employee", "warning")
    }

    if (!leaveType) {
      return Swal.fire("Required", "Please select Leave Type", "warning")
    }

    if (!reason.trim()) {
      return Swal.fire("Required", "Please enter Reason", "warning")
    }

    if (leaveType === "paidOff") {
      if (!leaveDate) {
        return Swal.fire("Required", "Please select Leave Date", "warning")
      }

      if (!workedDate) {
        return Swal.fire("Required", "Please select Worked Date", "warning")
      }

      if (new Date(leaveDate) > new Date(workedDate)) {
        return Swal.fire(
          "Invalid Dates",
          "Worked Date cannot be before Leave Date",
          "warning"
        )
      }
    }

    if (leaveType === "casualLeave" || leaveType === "sickLeave") {
      if (!fromDate) {
        return Swal.fire("Required", "Please select From Date", "warning")
      }

      if (!toDate) {
        return Swal.fire("Required", "Please select To Date", "warning")
      }

      if (new Date(fromDate) > new Date(toDate)) {
        return Swal.fire(
          "Invalid Dates",
          "From Date cannot be after To Date",
          "warning"
        )
      }
    }

    // ✅ Payload (clean & safe)
    const payload = {
      userId: employeeId,
      type: leaveType,
      reason,

      ...(leaveType === "paidOff"
        ? {
            leaveDate: workedDate,
            adjustmentDate: leaveDate,
          }
        : {
            fromDate,
            toDate,
          }),
    }

    const APIdata = await FetchPostData(setLoading, "leave", payload)

    if (APIdata?.data?.success) {
      Swal.fire("Success", APIdata?.data?.message, "success")

      setModalOpen(false)
      setNewLeave({
        employeeId: "",
        leaveType: "",
        fromDate: "",
        toDate: "",
        leaveDate: "",
        workedDate: "",
        reason: "",
      })

      fetchLeaves()
    } else {
      Swal.fire(
        "Error",
        APIdata?.response?.data?.message || "Something went wrong",
        "error"
      )
    }
  }

  /* ================== SEARCH ================== */
  // const filteredData = useMemo(() => {
  //   return leaveData.filter(item => {
  //     const matchesSearch =
  //       item?.user?.name?.toLowerCase().includes(searchString.toLowerCase()) ||
  //       item?.type?.toLowerCase().includes(searchString.toLowerCase()) ||
  //       item?.reason?.toLowerCase().includes(searchString.toLowerCase())

  //     const itemDate = new Date(item.fromDate ? item.fromDate : item?.leaveDate)
  //     const itemDate2 = new Date(
  //       item?.toDate ? item?.toDate : item?.adjustmentDate
  //     )
  //     const matchesMonth = monthFilter
  //       ? itemDate.getMonth() + 1 === Number(monthFilter)
  //       : true

  //     const matchesYear = yearFilter
  //       ? itemDate.getFullYear() === Number(yearFilter)
  //       : true

  //     const matchesMonth2 = monthFilter
  //       ? itemDate2.getMonth() + 1 === Number(monthFilter)
  //       : true

  //     const matchesYear2 = yearFilter
  //       ? itemDate2.getFullYear() === Number(yearFilter)
  //       : true

  //     return (
  //       matchesSearch &&
  //       matchesMonth &&
  //       matchesYear &&
  //       matchesMonth2 &&
  //       matchesYear2
  //     )
  //   })
  // }, [leaveData, searchString, monthFilter, yearFilter])

  const filteredData = useMemo(() => {
    let result = leaveData.filter(item => {
      const matchesSearch =
        item?.user?.name?.toLowerCase().includes(searchString.toLowerCase()) ||
        item?.type?.toLowerCase().includes(searchString.toLowerCase()) ||
        item?.reason?.toLowerCase().includes(searchString.toLowerCase())

      const itemDate = new Date(item.fromDate ? item.fromDate : item?.leaveDate)
      const itemDate2 = new Date(
        item?.toDate ? item?.toDate : item?.adjustmentDate
      )

      const matchesMonth = monthFilter
        ? itemDate.getMonth() + 1 === Number(monthFilter)
        : true

      const matchesYear = yearFilter
        ? itemDate.getFullYear() === Number(yearFilter)
        : true

      const matchesMonth2 = monthFilter
        ? itemDate2.getMonth() + 1 === Number(monthFilter)
        : true

      const matchesYear2 = yearFilter
        ? itemDate2.getFullYear() === Number(yearFilter)
        : true

      return (
        matchesSearch &&
        matchesMonth &&
        matchesYear &&
        matchesMonth2 &&
        matchesYear2
      )
    })

    result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

    return result
  }, [leaveData, searchString, monthFilter, yearFilter])

  /* ================== PAGINATION ================== */
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize
  const paginatedData = filteredData.slice(start, start + pageSize)

  /* ================== STATUS ACTIONS ================== */

  const handleApprove = id => {
    Swal.fire({
      icon: "info",
      title: "Approve Leave?",
      showCancelButton: true,
      confirmButtonText: "Approve",
    }).then(async res => {
      if (res.isConfirmed) {
        const APIdata = await FetchPutData(setLoading, "leave", id, {
          status: true,
        })

        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Leave Approved Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          fetchLeaves()
        } else {
          Swal.fire({
            title: "Error approving Leave.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const handleReject = id => {
    Swal.fire({
      icon: "info",
      title: "Reject Leave?",
      showCancelButton: true,
      confirmButtonText: "Reject",
    }).then(async res => {
      if (res.isConfirmed) {
        const APIdata = await FetchPutData(setLoading, "leave", id, {
          status: false,
        })

        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Leave Rejected Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          fetchLeaves()
        } else {
          Swal.fire({
            title: "Error rejecting Leave.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const exportToExcel = () => {
    const rows = filteredData.map((item, i) => ({
      "#": i + 1,
      "Applied Date": moment(item?.createdAt).format("DD-MM-YYYY"),
      Employee: item?.user?.name || "",
      "Leave Type":
        item.type === "paidOff"
          ? "Paid Off"
          : item.type === "casualLeave"
          ? "Casual Leave"
          : item.type === "sickLeave"
          ? "Sick Leave"
          : "--",
      "Leave Date":
        item.type === "paidOff"
          ? `Worked: ${moment(item.leaveDate).format(
              "DD-MM-YYYY"
            )} → Leave: ${moment(item.adjustmentDate).format("DD-MM-YYYY")}`
          : moment(item.fromDate).format("DD-MM-YYYY"),
      Reason: item.reason || "",
      "SL Balance": item.user?.sickLeave ?? "",
      "CL Balance": item.user?.casualLeave ?? "",
      "PO Balance": item.user?.paidOff ?? "",
      Status:
        item.status === true
          ? "Approved"
          : item.status === false
          ? "Rejected"
          : "Pending",
    }))

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Leaves")
    XLSX.writeFile(wb, `Leave_List_${moment().format("DD-MM-YYYY")}.xlsx`)
  }

  const statusBadge = status => {
    if (status === true)
      return (
        <Badge color="success" className="p-2 fs-6">
          Approved
        </Badge>
      )
    if (status === false)
      return (
        <Badge color="danger" className="p-2 fs-6">
          Rejected
        </Badge>
      )
    return (
      <Badge color="warning" className="p-2 fs-6">
        Pending
      </Badge>
    )
  }

  /* ================== UI ================== */
  const { checkAccess } = usePermissionData()

  return (
    <PermittedComponent isAccess={checkAccess(8, "view")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb title="Leave Management" breadcrumbItem="Leaves" />

        <Card>
          <CardBody>
            {/* HEADER */}
            <div className="d-flex justify-content-between mb-3">
              <div className="d-flex gap-2">
                <Input
                  type="select"
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                  className="rounded-4 w-auto pe-5"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </Input>
                <Input
                  placeholder="Search..."
                  value={searchString}
                  onChange={e => setSearchString(e.target.value)}
                  className="rounded-4 w-auto"
                />
                <Input
                  type="select"
                  value={monthFilter}
                  onChange={e => {
                    setMonthFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="rounded-4 w-auto"
                >
                  <option value="">All Months</option>
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </Input>

                {/* Year Filter */}
                <Input
                  type="select"
                  value={yearFilter}
                  onChange={e => {
                    setYearFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="rounded-4 w-auto"
                >
                  <option value="">All Years</option>
                  {[...Array(4)].map((_, i) => {
                    const y = new Date().getFullYear() - 1 + i
                    return (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    )
                  })}
                </Input>
              </div>
              <div className="d-flex gap-2">
                <Button
                  color="success"
                  className="rounded-4"
                  onClick={exportToExcel}
                  disabled={filteredData.length === 0}
                >
                  <FaFileExcel className="me-1" /> Export
                </Button>
                {checkAccess(8, "add") && (
                  <Button
                    className="btn-blue rounded-4"
                    onClick={() => setModalOpen(true)}
                  >
                    + Leave
                  </Button>
                )}
              </div>
            </div>

            {/* TABLE */}
            <Table bordered responsive>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Applied Date</th>
                  <th>Employee</th>
                  <th>Leave Type</th>
                  <th>Leave Date</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{start + index + 1}</td>
                    <td>{moment(item?.createdAt).format("DD-MM-YYYY")}</td>
                    <td>
                      <span className="d-flex align-items-center gap-1">
                        <span style={{ fontWeight: 500 }}>
                          {item?.user?.name}
                        </span>
                        <FaInfoCircle
                          id={`lb-${item.id}`}
                          style={{
                            color: "#6366f1",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                          size={14}
                        />
                        <UncontrolledPopover
                          target={`lb-${item.id}`}
                          placement="right"
                          trigger="hover focus"
                        >
                          <PopoverHeader
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              background: "#1e293b",
                              color: "#fff",
                              border: "1px solid #000",
                            }}
                          >
                            Current Leave Balance
                          </PopoverHeader>
                          <PopoverBody
                            style={{
                              padding: "10px 14px",
                              minWidth: 180,
                              border: "1px solid #000",
                              borderTop: "none",
                            }}
                          >
                            {[
                              {
                                label: "Sick Leave",
                                val: item.user?.sickLeave,
                                color: "#ef4444",
                              },
                              {
                                label: "Casual Leave",
                                val: item.user?.casualLeave,
                                color: "#f59e0b",
                              },
                              {
                                label: "Paid Off",
                                val: item.user?.paidOff,
                                color: "#6366f1",
                              },
                            ].map(({ label, val, color }) => (
                              <div
                                key={label}
                                className="d-flex justify-content-between align-items-center mb-2"
                              >
                                <span
                                  style={{
                                    fontSize: 13,
                                    color: "#334155",
                                    fontWeight: 500,
                                  }}
                                >
                                  {label}
                                </span>
                                <span
                                  style={{
                                    background: val > 0 ? color : "#94a3b8",
                                    color: "#fff",
                                    borderRadius: 20,
                                    padding: "2px 10px",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    minWidth: 28,
                                    textAlign: "center",
                                  }}
                                >
                                  {val ?? "—"}
                                </span>
                              </div>
                            ))}
                          </PopoverBody>
                        </UncontrolledPopover>
                      </span>
                    </td>
                    <td>
                      {item.type === "paidOff"
                        ? "Paid Off"
                        : item.type === "casualLeave"
                        ? "Casual Leave"
                        : item.type === "sickLeave"
                        ? "Sick Leave"
                        : "--"}
                    </td>
                    <td>
                      {item.type === "paidOff"
                        ? `Worked : ${moment(item.leaveDate).format(
                            "DD-MM-YYYY"
                          )} → Leave : ${moment(item.adjustmentDate).format(
                            "DD-MM-YYYY"
                          )}`
                        : `${moment(item.fromDate).format("DD-MM-YYYY")}`}
                    </td>
                    <td>{item.reason}</td>
                    <td>{statusBadge(item.status)}</td>
                    <td>
                      {item.status === null && (
                        <>
                          {checkAccess(8, "edit") && (
                            <>
                              <Button
                                color="success"
                                onClick={() => handleEditOpen(item)}
                                className="me-2"
                                title="Edit"
                              >
                                <FaEdit size={14} />
                              </Button>
                              <Button
                                color="info"
                                onClick={() => handleApprove(item.id)}
                                className="me-2"
                                title="Approve"
                              >
                                <FaCheck size={14} />
                              </Button>
                              <Button
                                color="danger"
                                onClick={() => handleReject(item.id)}
                                title="Reject"
                              >
                                <FaX size={14} />
                              </Button>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {/* ================= Pagination ================= */}
            <nav className="d-flex justify-content-between mt-3">
              <h6>
                Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
                {Math.min(start + pageSize, filteredData.length)} of{" "}
                {filteredData.length} entries
              </h6>

              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage(prev => Math.max(prev - 1, 1))
                    }
                  >
                    Previous
                  </button>
                </li>

                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${
                        pageNum === currentPage ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  )
                })}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage(prev => Math.min(prev + 1, totalPages))
                    }
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </CardBody>
        </Card>

        {/* ================== EDIT LEAVE MODAL ================== */}
        <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(false)} centered>
          <ModalHeader toggle={() => setEditModalOpen(false)}>
            Edit Leave
          </ModalHeader>
          <ModalBody>
            <Row className="g-3">
              <Col md={12}>
                <label>
                  Employee <span className="text-danger">*</span>
                </label>
                <Input
                  type="select"
                  value={editLeave.employeeId}
                  onChange={e =>
                    setEditLeave(prev => ({ ...prev, employeeId: e.target.value }))
                  }
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={12}>
                <label>
                  Leave Type <span className="text-danger">*</span>
                </label>
                <Input
                  type="select"
                  name="leaveType"
                  value={editLeave.leaveType}
                  onChange={handleEditChange}
                >
                  <option value="">Select Leave Type</option>
                  <option value="casualLeave">Casual Leave</option>
                  <option value="sickLeave">Sick Leave</option>
                  <option value="paidOff">Paid Off</option>
                </Input>
              </Col>

              {editLeave.leaveType === "paidOff" ? (
                <>
                  <Col md={6}>
                    <label>
                      Leave Date <span className="text-danger">*</span>
                    </label>
                    <Input
                      type="date"
                      name="leaveDate"
                      value={editLeave.leaveDate}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6}>
                    <label>
                      Worked Date <span className="text-danger">*</span>
                    </label>
                    <Input
                      type="date"
                      name="workedDate"
                      value={editLeave.workedDate}
                      onChange={handleEditChange}
                    />
                  </Col>
                </>
              ) : (
                <>
                  <Col md={6}>
                    <label>From Date</label>
                    <Input
                      type="date"
                      name="fromDate"
                      value={editLeave.fromDate}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6}>
                    <label>To Date</label>
                    <Input
                      type="date"
                      name="toDate"
                      value={editLeave.toDate}
                      onChange={handleEditChange}
                    />
                  </Col>
                </>
              )}

              <Col md={12}>
                <label>
                  Reason <span className="text-danger">*</span>
                </label>
                <Input
                  name="reason"
                  value={editLeave.reason}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={12} className="text-end">
                <Button className="w-25 btn-blue me-2 text-nowrap" onClick={handleEditLeave}>
                  Update Leave
                </Button>
                <Button
                  color="secondary"
                  className="w-25"
                  onClick={() => setEditModalOpen(false)}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </ModalBody>
        </Modal>

        {/* ================== ADD LEAVE MODAL ================== */}
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
          <ModalHeader toggle={() => setModalOpen(false)}>
            Add Leave
          </ModalHeader>
          <ModalBody>
            <Row className="g-3">
              <Col md={12}>
                <label>
                  Employee <span className="text-danger">*</span>
                </label>
                <Input
                  type="select"
                  value={newLeave.employeeId}
                  onChange={e => {
                    setNewLeave(prev => ({
                      ...prev,
                      employeeId: e.target.value,
                    }))
                  }}
                >
                  <option value="">Select Employee </option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={12}>
                <label>
                  Leave Type <span className="text-danger">*</span>
                </label>
                <Input
                  type="select"
                  name="leaveType"
                  value={newLeave.leaveType}
                  onChange={handleChange}
                >
                  <option value="">Select Leave Type</option>
                  <option value="casualLeave">Casual Leave</option>
                  <option value="sickLeave">Sick Leave</option>
                  <option value="paidOff">Paid Off</option>
                </Input>
              </Col>

              {newLeave.leaveType === "paidOff" ? (
                <>
                  <Col md={6}>
                    <label>
                      Leave Date <span className="text-danger">*</span>
                    </label>
                    <Input
                      type="date"
                      name="leaveDate"
                      value={newLeave.leaveDate}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col md={6}>
                    <label>
                      Worked Date <span className="text-danger">*</span>
                    </label>
                    <Input
                      type="date"
                      name="workedDate"
                      value={newLeave.workedDate}
                      onChange={handleChange}
                    />
                  </Col>
                </>
              ) : (
                <>
                  <Col md={6}>
                    <label>From Date</label>
                    <Input
                      type="date"
                      name="fromDate"
                      value={newLeave.fromDate}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col md={6}>
                    <label>To Date</label>
                    <Input
                      type="date"
                      name="toDate"
                      value={newLeave.toDate}
                      onChange={handleChange}
                    />
                  </Col>
                </>
              )}

              <Col md={12}>
                <label>
                  Reason <span className="text-danger">*</span>
                </label>
                <Input
                  name="reason"
                  value={newLeave.reason}
                  onChange={handleChange}
                />
              </Col>

              <Col md={12} className="text-end">
                <Button className="w-25 btn-blue me-2" onClick={handleAddLeave}>
                  Submit Leave
                </Button>
                <Button
                  color="secondary"
                  className="w-25"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </ModalBody>
        </Modal>
      </div>
    </PermittedComponent>
  )
}

export default Leaves
